import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useNotification } from '@dynamon-as/ui';
import { DepartmentsSummary } from '@features/departments';
import { DraftsList, useDrafts } from '@features/requests';
import { useEmployeeId } from '@shared/hooks';
import { useI18n } from '@shared/i18n';
import styles from './Dashboard.module.css';

/**
 * Dashboard Page
 * Shows a summary of user's departments and the latest drafts,
 * ordered by deadline (closest first), with a quick edit action.
 */
export default function Dashboard(): React.ReactElement {
    const navigate = useNavigate();
    const { notify } = useNotification();
    const { t } = useI18n();

    const { orgEmployeeId } = useEmployeeId();
    const { drafts, isLoading, error } = useDrafts(orgEmployeeId);

    useEffect((): void => {
        if (error) {
            notify.error(error);
        }
    }, [error, notify]);

    const handleEditDraft = useCallback(
        (requestId: string): void => {
            navigate(`/requests/${requestId}/edit`);
        },
        [navigate],
    );

    return (
        <div className={styles.container}>
            {/* Hero Section */}
            <div className={styles.hero}>
                <div className={styles.heroContent}>
                    <h1 className={styles.title}>{t('dashboard.title')}</h1>
                    <p className={styles.subtitle}>{t('dashboard.subtitle')}</p>
                </div>
            </div>

            {/* Latest Drafts Section */}
            <section
                className={styles.section}
                aria-labelledby="drafts-title"
            >
                <h2
                    id="drafts-title"
                    className={styles.sectionTitle}
                >
                    {t('dashboard.latestDraftsTitle')}
                </h2>
                <DraftsList
                    drafts={drafts}
                    isLoading={isLoading}
                    onEdit={handleEditDraft}
                />
            </section>

            {/* Departments Summary Section */}
            <section
                className={styles.section}
                aria-labelledby="departments-title"
            >
                <h2
                    id="departments-title"
                    className={styles.sectionTitle}
                >
                    {t('dashboard.myDepartmentsTitle')}
                </h2>
                <DepartmentsSummary />
            </section>
        </div>
    );
}
